import {
  ModalConfirm,
  ModalSizeVariants,
  TableComponentV2,
} from '@labrav/react-components'
import { get, isEmpty } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { getResellerAgreementData } from '../../modules/resaleAuthorization/utils'
import { ResellerAgreementStatus } from '../../types/flyout'
import { useDispatch, useSelector } from '../../store'
import {
  PartnerType,
  isLoading,
  LoadingTypes,
  useAccountId,
  useCurrentCloudProvider,
  usePartner,
  useScreenSize,
  useTenantPartnerId,
  useUserType,
} from '@labrav/common'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { DetailedOffer } from '../PrivateOfferForm/DetailedOffer/DetailedOffer'
import { getResaleMenuOptions } from '../ResaleAuthorizationWrapperUIV2/helpers'
import type { ResaleAuthorizationsListState } from '../ResaleAuthorizationWrapperUIV2/types'
import {
  mappingResaleForAdmin,
  mappingResaleForLabraUI,
  mappingResaleForLabraUIMPPO,
} from './constants'
import { getResaleAuthorizationSearchInput } from './helpers'
import type { ResaleAuthorizationsListV2Props } from './types'

export {
  mappingResaleForAdmin,
  mappingResaleForLabraUI,
  mappingResaleForLabraUIMPPO,
  statusMappingForLabraV2UI,
} from './constants'
export type { ResaleAuthorizationsListV2Props } from './types'

export const ResaleAuthorizationsListV2: React.FC<
  ResaleAuthorizationsListV2Props
> = ({
  crmOpportunityId,
  resaleList,
  selectedResale,
  openModal,
  handleModalClose,
  pricesArray,
  partnerId,
  externalBaseURL,
  search,
  tableEl,
  inputValue,
  selectedOption,
  selectedOfferStatusCheckboxes,
  expiryDate,
  appliedFilteredValues,
}) => {
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const { pathname } = useLocation()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const { products, selectedProduct: productId } =
    useFlyOutProductsBasedOnSelectedCloud()
  const partnerUser = useUserType()
  const tenantPartnerId = useTenantPartnerId()
  const { partnerData } = usePartner(tenantPartnerId || '', partnerUser)

  const isAdmin = partnerUser === PartnerType.Admin
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { resellerAgreements, pageNumber, count } =
    useSelector<ResaleAuthorizationsListState>(
      state => state.cppoReseller as ResaleAuthorizationsListState
    )

  const { pageSize: pageSizeValue, screenWidth } = useScreenSize()
  const columnMaxWidth = useMemo(
    () =>
      `${(screenWidth * (selectedCloudTypeProductListing === 'AWS' ? 20 : 11)) / 100}px`,
    [screenWidth, selectedCloudTypeProductListing]
  )

  const {
    cloud: routeCloud,
    accountId: accountIdFromRoute,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    partner_id?: string
    cloud?: string
    accountId?: string
    secureKey?: string
    partnerId?: string
  }>()
  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId = accountIdFromRoute || accountIdFromHook || ''

  const fetchMoreData = useCallback(() => {
    if (productId !== undefined && !isEmpty(products)) {
      {
        dispatch(
          getResellerAgreementData({
            partnerId,
            pageSize: pageSizeValue,
            pageNumber: pageNumber + 1,
            productId,
            cloud: selectedCloudTypeProductListing,
            crmOpportunityId,
            ...(!isEmpty(inputValue) && !isEmpty(selectedOption)
              ? {
                  input: inputValue,
                  selectedInput:
                    getResaleAuthorizationSearchInput(selectedOption),
                  sectionName: 'resale-authorizations',
                }
              : {}),
            expiryDays: expiryDate,
            offerStatus: selectedOfferStatusCheckboxes,
          })
        )
      }
    }
  }, [
    partnerId,
    pageNumber,
    productId,
    crmOpportunityId,
    expiryDate,
    selectedOfferStatusCheckboxes,
  ])

  const partnershipType = (
    partnerData as { partnershipType?: string | string[] } | null
  )?.partnershipType
  const isCP = partnershipType?.includes('CONSULTING_PARTNER')

  const isOfferAndAuthorizations = pathname?.includes('offers-authorizations')

  const isAnyFilterApplied = !!(appliedFilteredValues || []).length

  const baseUrl = useMemo(() => {
    if (externalBaseURL) {
      return externalBaseURL
    }
    if (routeCloud && accountId) {
      return `/flyout/${routeCloud}/${accountId}/products-listing`
    }
    return '/flyout/products-listing'
  }, [externalBaseURL, routeCloud, accountId])

  const handleClickRow = (selectedId: string) => {
    const selectedItem = resellerAgreements?.find(
      item => item?.id === selectedId
    )
    if (selectedItem) {
      const selectedProductId =
        selectedCloudTypeProductListing === 'AWS'
          ? get(selectedItem, 'products[0].awsProductId', '') ||
            selectedItem.awsProductId
          : selectedCloudTypeProductListing === 'AZURE'
            ? selectedItem.labraProductId
            : ''
      const options = getResaleMenuOptions(
        selectedItem.id as string,
        baseUrl,
        selectedProductId || '',
        selectedCloudTypeProductListing === 'AWS'
          ? selectedItem?.draft || false
          : selectedItem?.offerStatus ===
              ResellerAgreementStatus.OFFER_IN_DRAFT,
        selectedCloudTypeProductListing,
        selectedItem,
        undefined,
        routeCloud,
        accountId,
        externalBaseURL,
        secureKeyFromURL,
        partnerIdFromURL
      )

      const firstOption = options[0]
      if (firstOption && 'route' in firstOption && firstOption.route) {
        navigate({ pathname: firstOption.route, search })
      }
    }
  }
  return (
    <div data-testid="resale-auth-list">
      <TableComponentV2
        keyName={'migrationDetails.products'}
        objects={resaleList}
        mapping={
          isAdmin
            ? mappingResaleForAdmin
            : selectedCloudTypeProductListing === 'AZURE'
              ? mappingResaleForLabraUIMPPO
              : mappingResaleForLabraUI
        }
        count={count || 0}
        fetchMoreData={fetchMoreData}
        selectedItem={[]}
        onChangeCheckbox={() => void 0}
        showCheckbox={false}
        isLoading={loading}
        breakWorkHeader={false}
        outerRef={tableEl}
        customColumns={['actions']}
        stickyColumns={['actions']}
        onChangeRow={handleClickRow}
        tableHeight={
          externalBaseURL
            ? 'calc(100vh - 255px)'
            : !isOfferAndAuthorizations
              ? isCP
                ? isAnyFilterApplied
                  ? 'calc(100vh - 280px)'
                  : 'calc(100vh - 265px)'
                : 'calc(100vh - 190px)'
              : 'calc(100vh - 312px)'
        }
        customCellProps={[
          { style: { width: columnMaxWidth } },
          { style: { width: columnMaxWidth } },
          { style: { width: columnMaxWidth } },
          { style: { width: columnMaxWidth } },
        ]}
      />
      <ModalConfirm
        title={'Preview'}
        subTitle={''}
        content={
          <DetailedOffer offer={selectedResale} pricesArray={pricesArray} />
        }
        open={openModal}
        titleAcceptButton={'Okay'}
        onAccept={handleModalClose}
        onDecline={handleModalClose}
        variant={ModalSizeVariants.medium}
        hideCancelButton={true}
      />
    </div>
  )
}
